import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { RowActions } from "@/components/RowActions";
import { fmtDateIST } from "@/lib/datetime";

/**
 * Sep 04, 2026 · Holiday Calendar admin screen.
 *
 * Two panels:
 *   1. Weekly Off — pick which weekdays are non-working (persists as
 *      `system_config.weekly_off.indices`, Mon=0 … Sun=6). Default Sunday.
 *   2. Holiday Master — add / list / delete date+name pairs. All
 *      scheduler / capacity math routes read from these two surfaces
 *      via `backend/routes/holidays.py::working_days_between` etc.
 */
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [weeklyOff, setWeeklyOff] = useState([6]);
  const [form, setForm] = useState({ date: "", name: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: hs }, { data: wo }] = await Promise.all([
        api.get("/holidays/"),
        api.get("/weekly-off/"),
      ]);
      setHolidays(hs || []);
      setWeeklyOff(wo?.indices || [6]);
    } catch (e) {
      toast.error("Failed to load holiday calendar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleWeekday = async (i) => {
    const next = weeklyOff.includes(i) ? weeklyOff.filter(x => x !== i) : [...weeklyOff, i];
    try {
      const { data } = await api.put("/weekly-off/", { indices: next });
      setWeeklyOff(data.indices);
      toast.success(`Weekly Off updated · ${data.indices.map(n => WEEKDAYS[n]).join(", ") || "no days"}`);
    } catch { toast.error("Failed to update weekly off"); }
  };

  const addHoliday = async () => {
    if (!form.date || !form.name.trim()) return toast.error("Date and name are required");
    try {
      await api.post("/holidays/", { date: form.date, name: form.name.trim() });
      toast.success(`Added ${form.name} on ${form.date}`);
      setForm({ date: "", name: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add holiday"); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    await api.delete(`/holidays/${id}`);
    toast.success("Deleted"); load();
  };

  const inp = "h-9 w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 text-sm focus:outline-none focus:border-[var(--accent)]";

  return (
    <div className="p-6 space-y-6" data-testid="holiday-calendar-page">
      <div>
        <h1 className="text-lg font-semibold tracking-widest uppercase text-white">Holiday Calendar</h1>
        <p className="text-xs text-zinc-500 mt-1">Non-working days · scheduler skips these when computing capacity + finish dates</p>
      </div>

      {/* Weekly Off */}
      <div className="tactical-card p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Standard Weekly Off</div>
        <div className="flex gap-2 flex-wrap" data-testid="weekly-off-row">
          {WEEKDAYS.map((d, i) => {
            const on = weeklyOff.includes(i);
            return (
              <button
                key={i}
                data-testid={`wo-toggle-${d}`}
                onClick={() => toggleWeekday(i)}
                className={`h-9 px-4 rounded-sm text-xs font-mono uppercase tracking-widest transition-colors ${on ? "bg-amber-500/30 border border-amber-400/60 text-amber-100" : "bg-[var(--bg-0)] border border-[var(--border)] text-zinc-400 hover:border-[var(--accent)]"}`}
              >
                {d}{on ? " ·  ON" : ""}
              </button>
            );
          })}
        </div>
        <div className="text-[10px] text-zinc-500">Any weekday toggled ON is treated as a non-working day everywhere in Production Planning.</div>
      </div>

      {/* Holiday Master */}
      <div className="tactical-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Public / Company Holidays</div>
          <div className="text-[10px] text-zinc-500">{holidays.length} registered</div>
        </div>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div className="col-span-1">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Date</label>
            <input type="date" data-testid="holiday-date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inp} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</label>
            <input type="text" data-testid="holiday-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Republic Day, Diwali, …" className={inp} />
          </div>
          <div>
            <button data-testid="holiday-add-btn" onClick={addHoliday} className="h-9 px-4 w-full bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm">Add Holiday</button>
          </div>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-2)]/50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-right px-3 py-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="holiday-rows">
              {loading ? (
                <tr><td colSpan={3} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
              ) : holidays.length === 0 ? (
                <tr><td colSpan={3} className="px-3 py-8 text-center text-zinc-500">No holidays registered yet.</td></tr>
              ) : holidays.map(h => (
                <tr key={h.id} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`holiday-row-${h.id.slice(0, 8)}`}>
                  <td className="px-3 py-2 font-mono text-white">{fmtDateIST(h.date)}</td>
                  <td className="px-3 py-2 text-zinc-300">{h.name}</td>
                  <td className="px-3 py-2 text-right">
                    <RowActions
                      testid={`holiday-actions-${h.id.slice(0, 8)}`}
                      items={[
                        { label: "Delete", tone: "danger", testid: `holiday-delete-${h.id.slice(0, 8)}`, onClick: () => remove(h.id, h.name) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
